import React, { CSSProperties, ReactNode, useState } from 'react';
import { useGetDefaultQuery } from '../../generated/graphql';
import EditDefaultDialog from '../../components/dialogs/EditDefaultDialog/EditDefaultDialog';
import { Link } from 'react-router-dom';
import Tags from './Tags/Tags';
import Groups from './Groups/Groups';
import HealthChecks from './HealthChecks/HealthChecks';
import { Pencil, ChevronDown, ChevronUp, Settings2, Tags as TagsIcon, Layers, Activity } from 'lucide-react';

type SettingsProps = {
    style?: CSSProperties | undefined,
    children?: ReactNode | undefined,
    className?: string;
};

const tabs = [
    { label: 'Tags', icon: TagsIcon },
    { label: 'Groups', icon: Layers },
    { label: 'Health Checks', icon: Activity },
];

const Settings = (props: SettingsProps) => {
    const { style } = props;
    const [selected, setSelected] = useState<number>(0);
    const [expanded, setExpanded] = useState(true);
    const [editDefaultVisible, setEditDefaultVisible] = useState(false);

    const defaultSetting = useGetDefaultQuery();

    const isProjSubNameEnabled = false; // isProjSubNameEnabled removed from backend schema

    const handleEditDefaultOnClose = () => {
        setEditDefaultVisible(false);
        defaultSetting.refetch();
    };

    return (
        <>
            <div className="page-container" style={style} data-testid="Settings">
                {/* Header */}
                <div className="page-header">
                    <div>
                        <h1 className="page-title">Settings</h1>
                        <p className="page-subtitle">Configure global system preferences</p>
                    </div>
                    <button className="btn-primary" onClick={() => setEditDefaultVisible(true)}>
                        <Pencil size={16} />
                        Edit Defaults
                    </button>
                </div>

                {/* Essentials Panel */}
                <div className="card overflow-hidden">
                    <button
                        onClick={() => setExpanded(!expanded)}
                        className="w-full flex items-center justify-between px-6 py-4"
                        style={{
                            background: 'none', border: 'none', cursor: 'pointer', outline: 'none',
                            borderBottom: expanded ? '1px solid var(--border-default)' : 'none',
                        }}
                    >
                        <div className="flex items-center gap-2">
                            <Settings2 size={16} style={{ color: 'var(--text-tertiary)' }} />
                            <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Defaults</span>
                        </div>
                        {expanded ? <ChevronUp size={16} style={{ color: 'var(--text-tertiary)' }} /> : <ChevronDown size={16} style={{ color: 'var(--text-tertiary)' }} />}
                    </button>

                    {expanded && (
                        <div className="px-6 py-5 animate-fade-in">
                            <div className="grid grid-cols-[auto_1fr] gap-x-8 gap-y-3">
                                <span className="text-xs font-medium" style={{ color: 'var(--text-tertiary)' }}>Default Client</span>
                                <Link
                                    to={`/clients/${defaultSetting?.data?.Default?.client?.id}`}
                                    className="text-sm hover:underline"
                                    style={{ color: 'var(--accent-text)' }}
                                >
                                    {defaultSetting?.data?.Default?.client?.name}
                                </Link>

                                <span className="text-xs font-medium" style={{ color: 'var(--text-tertiary)' }}>Default Library</span>
                                <Link
                                    to={`/clients/${defaultSetting?.data?.Default?.client?.id}/library/${defaultSetting?.data?.Default?.library?.id}`}
                                    className="text-sm hover:underline"
                                    style={{ color: 'var(--accent-text)' }}
                                >
                                    {defaultSetting?.data?.Default?.library?.name}
                                </Link>

                                <span className="text-xs font-medium" style={{ color: 'var(--text-tertiary)' }}>Project Sub-name</span>
                                <span className="text-sm" style={{ color: 'var(--text-primary)' }}>
                                    {isProjSubNameEnabled ? (
                                        <span className="badge-success">Shown by default</span>
                                    ) : (
                                        <span className="badge-default">Hidden by default</span>
                                    )}
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Tabs */}
                <div className="flex flex-col flex-1 min-h-0">
                    <div className="tab-bar mb-4" style={{ alignSelf: 'flex-start' }}>
                        {tabs.map((tab, idx) => {
                            const Icon = tab.icon;
                            return (
                                <button
                                    key={tab.label}
                                    onClick={() => setSelected(idx)}
                                    className={selected === idx ? 'tab-item-active' : 'tab-item'}
                                >
                                    <Icon size={14} strokeWidth={1.5} />
                                    {tab.label}
                                </button>
                            );
                        })}
                    </div>

                    <div className="flex-1 min-h-0 animate-fade-in">
                        {selected === 0 && <Tags />}
                        {selected === 1 && <Groups />}
                        {selected === 2 && <HealthChecks />}
                    </div>
                </div>
            </div>
            <EditDefaultDialog visible={editDefaultVisible} onClose={handleEditDefaultOnClose} />
        </>
    );
};

export default Settings;